import { supabase } from '../../utils/supabaseClient';

const toDb = (b) => {
  const basePayload = {
    // customer_id intentionally omitted for updates in modify flow; only set during create
    customer_id: b.customerId,
    // awb_no should not be updated in modify flow
    awb_no: b.awbNo,
    date: b.date,
    receiver_name: b.receiverName,
    receiver_address: b.receiverAddress || null,
    pincode: b.pincode || null,
    mode: b.mode,
    type: b.type || null,
    weight: parseFloat(b.weight),
    quantity: parseInt(b.quantity || 1, 10),
    amount: parseFloat(b.amount),
  };

  // Only include other_charge if the column exists (after migration)
  // This prevents errors if the migration hasn't been run yet
  if (b.otherCharge !== undefined) {
    basePayload.other_charge = parseFloat(b.otherCharge || 0);
  }

  return basePayload;
};

export async function getNextAwbNo(userId) {
  const { data, error } = await supabase
    .from('bookings')
    .select('awb_no')
    .eq('user_id', userId)
    .order('awb_no', { ascending: false })
    .limit(1);
  if (error) throw error;
  if (!data || data.length === 0 || !data[0].awb_no) return '000001';
  const last = parseInt(String(data[0].awb_no), 10) || 0;
  const next = last + 1;
  return next.toString().padStart(6, '0');
}

export async function createBooking(userId, booking) {
  const payload = { user_id: userId, ...toDb(booking) };

  // 1. Check if AWB already exists
  const { data: existing, error: checkError } = await supabase
    .from('bookings')
    .select('id, receiver_name, customer_id')
    .eq('user_id', userId)
    .eq('awb_no', payload.awb_no)
    .single();

  if (checkError && checkError.code !== 'PGRST116') { // PGRST116 = no rows returned
    throw checkError;
  }

  if (existing) {
    // If receiver_name is set, it means this docket has already been fully used/booked
    if (existing.receiver_name) {
      throw new Error(`Duplicate AWB: '${payload.awb_no}' has already been booked.`);
    }

    // If it's assigned to a customer in Stationary Sales, ensure it matches the booking customer
    if (existing.customer_id && existing.customer_id !== payload.customer_id) {
      throw new Error(`AWB '${payload.awb_no}' is assigned to a different customer. Please verify.`);
    }

    // It's a blank stationary docket. Update it with the new details.
    const { data: updateData, error: updateError } = await supabase
      .from('bookings')
      .update(payload)
      .eq('id', existing.id)
      .select('*')
      .single();

    if (updateError) throw updateError;
    return updateData;
  }

  // 2. If it does not exist, insert it as a brand new docket
  const { data, error } = await supabase
    .from('bookings')
    .insert(payload)
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

export async function listBookingsForRange(userId, customerId, fromDate, toDate) {
  let query = supabase
    .from('bookings')
    // Include customer's user-entered customer_id so callers can show/export friendly IDs
    .select('*, customers(customer_id, customer_name)')
    .eq('user_id', userId)
    .order('date', { ascending: true });

  if (customerId) query = query.eq('customer_id', customerId);
  if (fromDate) query = query.gte('date', fromDate);
  if (toDate) query = query.lte('date', toDate);

  const { data, error } = await query;
  if (error) throw error;
  return (data || []).map(row => ({
    ...row,
    customer_name: row.customers?.customer_name || 'Unknown'
  }));
}

export async function getBookingByAwb(userId, awbNo) {
  const { data, error } = await supabase
    .from('bookings')
    .select('*, customers(customer_id, customer_name)')
    .eq('user_id', userId)
    .eq('awb_no', awbNo)
    .single();
  if (error) throw error;
  return data;
}

export async function updateBooking(userId, bookingId, values) {
  // Build payload without customer_id from toDb, because toDb uses the string ID (customerId)
  const effectiveValues = { ...values };
  // Check if we have a direct customer_id (UUID) from bulk update or customerIdUuid from form
  const newCustomerId = effectiveValues.customer_id || effectiveValues.customerIdUuid;

  delete effectiveValues.customerId;
  delete effectiveValues.customerIdUuid;

  const full = { ...toDb(effectiveValues) };

  // Explicitly set customer_id only if we have a valid UUID to update
  if (newCustomerId) {
    full.customer_id = newCustomerId;
  } else {
    // If no valid UUID provided, ensure we don't accidentally send the string ID from toDb
    delete full.customer_id;
  }

  // awb_no should never be updated via this function
  delete full.awb_no;
  const payload = full;
  const { data, error } = await supabase
    .from('bookings')
    .update(payload)
    .eq('user_id', userId)
    .eq('id', bookingId)
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

export async function deleteBooking(userId, bookingId) {
  const { error } = await supabase
    .from('bookings')
    .delete()
    .eq('user_id', userId)
    .eq('id', bookingId);
  if (error) throw error;
}

/**
 * Get recent bookings for a user
 * @param {string} userId - User ID
 * @param {number} limit - Maximum number of bookings to return
 * @returns {Promise<Array>} - Array of recent bookings
 */
export async function getRecentBookings(userId, limit = 5) {
  const { data, error } = await supabase
    .from('bookings')
    .select('*, customers(customer_name)')
    .eq('user_id', userId)
    .order('date', { ascending: false })
    .limit(limit);
  if (error) throw error;

  // Format the data to include customer_name directly in the booking object
  return (data || []).map(booking => ({
    ...booking,
    customer_name: booking.customers?.customer_name || 'Unknown',
    // Add booking_date alias for date to maintain compatibility with the UI
    booking_date: booking.date
  }));
}

/**
 * Create multiple bookings in bulk
 * @param {string} userId - User ID
 * @param {Array} bookings - Array of booking objects
 * @returns {Promise<Object>} - Results with success and failed counts
 */
export async function createMultipleBookings(userId, bookings) {
  if (!bookings || bookings.length === 0) {
    return { success: 0, failed: 0, errors: [] };
  }

  let success = 0;
  let failed = 0;
  const errors = [];

  // Insert one-by-one to avoid entire batch failing on a single bad row (e.g., duplicate AWB)
  // This preserves as many rows as possible for the user.
  for (const booking of bookings) {
    try {
      const base = toDb(booking);
      // Ensure minimally valid payload
      const awb = String(base.awb_no || '').trim();
      const dateStr = String(base.date || '').trim();
      if (!awb || !dateStr) {
        failed += 1;
        errors.push(`Missing AWB or Date for row with AWB '${awb || 'N/A'}'`);
        continue;
      }

      const payload = { user_id: userId, ...base };
      const { error } = await supabase
        .from('bookings')
        .insert(payload)
        .select('*')
        .single();

      if (error) {
        failed += 1;
        // 23505 is unique_violation in Postgres (likely duplicate AWB)
        const msg = error.code === '23505' ? `Duplicate AWB '${awb}'` : error.message || 'Insert failed';
        errors.push(msg);
      } else {
        success += 1;
      }
    } catch (e) {
      failed += 1;
      errors.push(e.message || 'Unknown error');
    }
  }

  return { success, failed, errors };
}

/**
 * Save stationary dockets into bookings table with blank columns
 * @param {string} userId - User ID
 * @param {Array<string>} awbNumbers - Array of AWB numbers
 * @param {string} purchaseDate - Purchase date
 * @returns {Promise<Object>} - Results with success and failed counts
 */
export async function saveStationaryBookings(userId, awbNumbers, purchaseDate) {
  if (!awbNumbers || awbNumbers.length === 0) {
    return { success: 0, failed: 0, errors: [] };
  }

  let success = 0;
  let failed = 0;
  const errors = [];

  for (const awb of awbNumbers) {
    try {
      if (!awb || !purchaseDate) {
        failed += 1;
        errors.push(`Missing AWB or Date for row with AWB '${awb || 'N/A'}'`);
        continue;
      }

      // Create payload with only user_id, awb_no, and purchase_date.
      // Other columns will be null or their default database values.
      const payload = {
        user_id: userId,
        awb_no: awb.trim(),
        purchase_date: purchaseDate.trim()
      };

      const { error } = await supabase
        .from('bookings')
        .insert(payload)
        .select('id')
        .single();

      if (error) {
        failed += 1;
        const msg = error.code === '23505' ? `Duplicate AWB '${awb}'` : error.message || 'Insert failed';
        errors.push(msg);
      } else {
        success += 1;
      }
    } catch (e) {
      failed += 1;
      errors.push(e.message || 'Unknown error');
    }
  }

  return { success, failed, errors };
}

/**
 * Get count of available stationary dockets (no customer, no receiver)
 * @param {string} userId - User ID
 * @returns {Promise<number>} - Count of available dockets
 */
export async function getUnassignedDocketsCount(userId) {
  const { count, error } = await supabase
    .from('bookings')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId)
    .is('customer_id', null)
    .is('receiver_name', null);

  if (error) throw error;
  return count || 0;
}

/**
 * Assign a range of stationary dockets to a specific customer
 * @param {string} userId - User ID
 * @param {string} customerId - Customer UUID
 * @param {Array<string>} awbNumbers - Array of AWBs to assign
 * @returns {Promise<Object>} - Results
 */
export async function assignDocketsToCustomer(userId, customerId, awbNumbers) {
  if (!awbNumbers || awbNumbers.length === 0) {
    return { success: 0, failed: 0, errors: [] };
  }

  let success = 0;
  let failed = 0;
  const errors = [];

  for (const awb of awbNumbers) {
    try {
      // First check if it's actually unassigned
      const { data: existing, error: checkError } = await supabase
        .from('bookings')
        .select('id, customer_id, receiver_name')
        .eq('user_id', userId)
        .eq('awb_no', awb)
        .single();

      if (checkError) {
        failed++;
        errors.push(`AWB '${awb}' not found.`);
        continue;
      }

      if (existing.receiver_name) {
        failed++;
        errors.push(`AWB '${awb}' is already fully used.`);
        continue;
      }

      if (existing.customer_id) {
        if (existing.customer_id === customerId) {
          success++;
          continue;
        } else {
          failed++;
          errors.push(`AWB '${awb}' is already assigned to another customer.`);
          continue;
        }
      }

      // Safe to assign
      const { error: updateError } = await supabase
        .from('bookings')
        .update({ customer_id: customerId })
        .eq('id', existing.id);

      if (updateError) {
        failed++;
        errors.push(`Failed to assign '${awb}': ${updateError.message}`);
      } else {
        success++;
      }
    } catch (e) {
      failed++;
      errors.push(e.message || 'Unknown error');
    }
  }

  return { success, failed, errors };
}

/**
 * Get statistics for stationary dockets
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - Stats object
 */
export async function getStationaryStats(userId) {
  const { data, error } = await supabase
    .from('bookings')
    .select('id, customer_id, receiver_name')
    .eq('user_id', userId)
    .not('purchase_date', 'is', null);

  if (error) throw error;

  let total = 0;
  let available = 0;
  let assigned = 0;
  let used = 0;

  (data || []).forEach(row => {
    total++;
    if (row.receiver_name) {
      used++;
    } else if (row.customer_id) {
      assigned++;
    } else {
      available++;
    }
  });

  return { total, available, assigned, used };
}

/**
 * Get detailed inventory of stationary dockets with filtering
 * @param {string} userId - User ID
 * @returns {Promise<Array>} - Array of docket objects
 */
export async function getStationaryInventory(userId) {
  // Fetch all stationary dockets. For massive datasets, we would paginate at DB level,
  // but we'll fetch all here for comprehensive client-side searching and filtering.
  const { data, error } = await supabase
    .from('bookings')
    .select('id, awb_no, purchase_date, customer_id, receiver_name, customers(customer_name)')
    .eq('user_id', userId)
    .not('purchase_date', 'is', null)
    .order('purchase_date', { ascending: false })
    .order('awb_no', { ascending: true });

  if (error) throw error;

  return (data || []).map(row => {
    let status = 'Available';
    if (row.receiver_name) {
      status = 'Used';
    } else if (row.customer_id) {
      status = 'Assigned';
    }

    return {
      id: row.id,
      awb_no: row.awb_no,
      purchase_date: row.purchase_date,
      status: status,
      customer_name: row.customers?.customer_name || '-',
      customer_id: row.customer_id,
      receiver_name: row.receiver_name
    };
  });
}
